import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Every open overlay, outermost first.
 *
 * The job card opens the activity sheet over itself and the booking dialog over
 * that, and three overlays all listening on `document` for Escape would close
 * every one of them at once — an inner listener cannot stop an event reaching a
 * sibling listener on the same node. So depth is tracked here and only the last
 * one in the stack answers the key.
 *
 * Shared by `Modal` and `Sheet` deliberately. Two stacks would each believe
 * they were the top of the world.
 */
const stack: symbol[] = []

/**
 * Whether a Modal or Sheet is open. A full-screen surface that keeps its own
 * keys, such as a booking session, leaves them alone while one is open over
 * it, so Escape closes the job card and not the session under it.
 */
export function overlayOpen(): boolean {
  return stack.length > 0
}

type Inside = RefObject<HTMLElement | null>

/** Why a light layer closed, for the one caller that cares: focus goes back on Escape. */
export type DismissReason = 'escape' | 'outside' | 'scroll'

/**
 * Closes a light layer — a menu, a list, a picker — on a press outside it, on
 * Escape, and, if asked, when anything scrolls under it. Not a window, so it
 * is not on the stack and locks nothing.
 *
 * Escape is taken on its way down (capture) and stopped there, so it closes
 * the menu and not the dialog the menu sits in. Stopping it on the way up
 * does not work: the dialog listens on the same node in the same phase, and
 * `stopPropagation` does not reach a sibling listener — a list inside a
 * dialog used to close both. Presses are pointer events, so a finger counts.
 *
 * `inside` is every element a press may land on without closing: the panel
 * and, usually, the button that opened it. `fromNextTick` is for a layer
 * opened by a press that is still on its way up when the layer mounts.
 */
export function useDismiss(
  inside: Inside | readonly Inside[],
  open: boolean,
  close: (reason: DismissReason) => void,
  { closeOnScroll = false, fromNextTick = false }: { closeOnScroll?: boolean; fromNextTick?: boolean } = {},
): void {
  const latest = useRef(close)
  latest.current = close
  const refs = useRef<readonly Inside[]>([])
  refs.current = Array.isArray(inside) ? inside : [inside as Inside]

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && refs.current.some((ref) => ref.current?.contains(target))) return
      latest.current('outside')
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      latest.current('escape')
    }
    const onScroll = (event: Event) => {
      // A scroll inside the menu is the menu's own list moving.
      const target = event.target
      if (target instanceof Node && refs.current.some((ref) => ref.current?.contains(target))) return
      latest.current('scroll')
    }

    const listen = () => {
      document.addEventListener('pointerdown', onPointerDown)
      document.addEventListener('keydown', onKeyDown, true)
      if (closeOnScroll) window.addEventListener('scroll', onScroll, true)
    }
    const timer = fromNextTick ? window.setTimeout(listen, 0) : null
    if (timer === null) listen()

    return () => {
      if (timer !== null) window.clearTimeout(timer)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, closeOnScroll, fromNextTick])
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, video[controls], audio[controls], [tabindex]:not([tabindex="-1"])'

/**
 * Keeps Tab inside an open overlay. `aria-modal` tells a screen reader the
 * page behind is out of reach; this tells the keyboard, which otherwise walks
 * straight out into the screen underneath. Call it from the panel's keydown.
 */
export function trapTab(event: { key: string; shiftKey: boolean; preventDefault: () => void }, panel: HTMLElement): void {
  if (event.key !== 'Tab') return
  const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => !element.closest('[hidden], [inert]'),
  )
  if (items.length === 0) {
    event.preventDefault()
    panel.focus()
    return
  }
  const first = items[0]!
  const last = items[items.length - 1]!
  const active = document.activeElement
  if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

/**
 * Escape, scroll lock, and the depth an overlay sits at.
 *
 * The returned depth is what keeps a dialog above the sheet that opened it,
 * because both are portalled to `body` and neither is inside the other's DOM.
 */
export function useOverlay(open: boolean, onClose: () => void): number {
  const [token] = useState(() => Symbol('overlay'))
  const [depth, setDepth] = useState(0)

  // Read through a ref, not a dependency. Most callers pass an inline arrow,
  // and re-running the effect for a new one popped this overlay and pushed it
  // back on top — so a job card that re-rendered under an open photo took
  // Escape off the photo and closed itself.
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    if (!open) return

    stack.push(token)
    setDepth(stack.length - 1)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (stack[stack.length - 1] !== token) return
      event.stopPropagation()
      close.current()
    }

    document.addEventListener('keydown', onKeyDown)

    // Stops the page scrolling behind the overlay, which otherwise loses the
    // dispatcher's place while they read. Only the outermost sets it, so
    // closing an inner dialog does not hand scrolling back too early.
    const { overflow } = document.body.style
    if (stack.length === 1) document.body.style.overflow = 'hidden'

    // Focus goes back to whatever opened the overlay — the photo tile, the
    // row — so a keyboard user carries on from where they were. Without
    // scrolling: the page underneath never moved.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const index = stack.indexOf(token)
      if (index !== -1) stack.splice(index, 1)
      if (stack.length === 0) document.body.style.overflow = overflow
      if (opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [open, token])

  return depth
}
